import { readCsrfToken } from './csrf';
import { ApiError, type ApiErrorBody } from './types';

/**
 * Cliente HTTP fino contra o BFF `/web/*`, same-origin (AMVP-01/02). Nunca aceita
 * base URL configurável para outra origem — não existe cenário em que este app
 * fale com algo além de `/web` no mesmo host. Nunca guarda API key de tenant: essa
 * chave não existe do lado do browser (IMVP-03).
 *
 * `credentials: 'include'` sempre — a sessão viaja por cookie HttpOnly, não há
 * token para anexar manualmente em `Authorization`. GET nunca manda CSRF (não muda
 * estado); toda mutação (`POST`/`PATCH`/`DELETE`) ecoa o cookie CSRF não-HttpOnly
 * no header `x-csrf-token` (AMVP-03).
 */

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface RequestOptions {
  method?: string;
  json?: unknown;
  formData?: FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function parseErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    const data = (await response.json()) as unknown;
    if (
      data &&
      typeof data === 'object' &&
      'error' in data &&
      'message' in data &&
      typeof (data as Record<string, unknown>).error === 'string' &&
      typeof (data as Record<string, unknown>).message === 'string'
    ) {
      return data as ApiErrorBody;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * `retry-after` em SEGUNDOS, e só nessa forma.
 *
 * O RFC admite também data HTTP, mas o backend do ARGINK sempre manda o inteiro
 * (`OwnerAuthRateLimitedError.retryAfterSeconds`). Aceitar a forma de data aqui
 * seria escrever um parser para um caso que não existe; aceitar qualquer string
 * seria devolver `NaN` para a UI contar. Fora do inteiro positivo: `null`, e a
 * tela mostra o estado sem contagem em vez de contar um número inventado.
 */
function parseRetryAfterSeconds(response: Response): number | null {
  const raw = response.headers.get('retry-after');
  if (!raw) return null;
  const seconds = Number(raw.trim());
  if (!Number.isInteger(seconds) || seconds <= 0) return null;
  return seconds;
}

export async function apiRequest(path: string, options: RequestOptions = {}): Promise<Response> {
  const method = (options.method ?? (options.json || options.formData ? 'POST' : 'GET')).toUpperCase();
  const headers: Record<string, string> = { ...options.headers };

  if (MUTATING_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData; // navegador define multipart boundary sozinho
  } else if (options.json !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.json);
  }

  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers,
    body,
    signal: options.signal,
  });

  if (!response.ok) {
    const errorBody = await parseErrorBody(response);
    throw new ApiError(response.status, errorBody, parseRetryAfterSeconds(response));
  }

  return response;
}

export async function apiJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await apiRequest(path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
